import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, XCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  fr: {
    title: "Abandonner ma demande",
    subtitle: "Nous vous confirmons que votre demande sera clôturée. Cette action est réversible : vous pourrez déposer une nouvelle demande à tout moment.",
    label: "Pourquoi n'allez-vous pas au bout ? (facultatif)",
    placeholder: "Situation qui a changé, mauvais timing, n'a pas trouvé l'information cherchée…",
    confirm: "Confirmer l'abandon",
    cancel: "Retour",
    already: "Cette demande a déjà été clôturée. Vous pouvez déposer une nouvelle demande depuis notre page d'accueil.",
    invalidLink: "Ce lien n'est plus valide.",
    done: "Votre demande a été clôturée. Merci pour votre retour.",
  },
  en: {
    title: "Withdraw my request",
    subtitle: "We will close your file. This is not final: you may submit a new request at any time.",
    label: "Why not going further? (optional)",
    placeholder: "Situation changed, wrong timing, could not find what I was looking for…",
    confirm: "Confirm withdrawal",
    cancel: "Back",
    already: "This request has already been closed. You may submit a new request from our homepage.",
    invalidLink: "This link is no longer valid.",
    done: "Your request has been withdrawn. Thank you for the feedback.",
  },
};

const pick = (locale, key) => (COPY[locale] || COPY.fr)[key] || COPY.fr[key];

export default function ApplicationWithdraw() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const wt = sp.get("wt") || "";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/application/${token}/withdrawal-info`, {
          params: wt ? { wt } : {},
        });
        if (!alive) return;
        setInfo(data);
      } catch (err) {
        setError(err?.response?.data?.detail || "Lien invalide.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token, wt]);

  const locale = useMemo(() => {
    const raw = (info?.locale || "fr").toLowerCase();
    return raw.startsWith("en") ? "en" : "fr"; // page copy only in FR/EN; email/full i18n stays server-side
  }, [info]);

  const t = (k) => pick(locale, k);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/application/${token}/withdraw`, {
        reason: reason.trim() || null,
        wt: wt || null,
      });
      setDone(true);
      toast.success(t("done"));
    } catch (err) {
      const detail = err?.response?.data?.detail || t("invalidLink");
      toast.error(typeof detail === "string" ? detail : t("invalidLink"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="withdraw-invalid">
          <XCircle className="mx-auto text-rose-300 mb-4" size={28} />
          <div className="font-serif-display text-2xl mb-2">{t("invalidLink")}</div>
          <div className="text-white/50 text-sm">{error}</div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 inline-flex items-center gap-2 text-[#e6c98a] hover:underline text-sm"
          >
            <ArrowLeft size={14} /> Homepage
          </button>
        </div>
      </div>
    );
  }

  if (done || info.already_abandoned || info.already_final) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6" data-testid="withdraw-done">
        <div className="max-w-md text-center">
          <div className="font-serif-display text-2xl mb-3 text-[#e6c98a]">{t("title")}</div>
          <p className="text-white/70 leading-relaxed">
            {done ? t("done") : info.already_abandoned ? t("done") : t("already")}
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-8 inline-flex items-center gap-2 text-[#e6c98a] hover:underline text-sm"
          >
            <ArrowLeft size={14} /> Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-16" data-testid="withdraw-form">
      <div className="max-w-lg w-full border border-white/10 bg-neutral-950/60 rounded-md p-8">
        <div className="text-[10px] tracking-luxe uppercase text-white/40 mb-3 font-mono">
          {info?.reference_number}
        </div>
        <h1 className="font-serif-display text-3xl mb-4">{t("title")}</h1>
        <p className="text-white/60 text-sm leading-relaxed mb-8">{t("subtitle")}</p>

        <label className="block text-[10px] tracking-luxe uppercase text-white/45 mb-2">
          {t("label")}
        </label>
        <textarea
          data-testid="withdraw-reason"
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 2000))}
          placeholder={t("placeholder")}
          className="w-full bg-black/60 border border-white/10 rounded-md p-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-white/25"
        />

        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-sm text-white/60 hover:text-white"
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="withdraw-confirm"
            className="inline-flex items-center gap-2 px-6 py-3 bg-rose-500/90 hover:bg-rose-500 text-white rounded text-sm tracking-mid uppercase transition-colors disabled:opacity-60"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
            {t("confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
